import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  CheckCircle, CreditCard, Mail, Phone, AlertCircle, Loader2, 
  ArrowRight, ShieldCheck, Clock, Building2, Stethoscope, Receipt
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmergencyCheckout = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [patient, setPatient] = useState(null);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentSent, setPaymentSent] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  
  useEffect(() => {
    loadInvoice();
  }, [invoiceId]);
  
  const loadInvoice = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/checkout/emergency/${invoiceId}`);
      setInvoice(res.data.invoice);
      setPatient(res.data.patient);
      
      // Check if already paid
      if (res.data.invoice?.status === 'paid') {
        setPaymentSent(true);
      }
    } catch (err) {
      console.error('Error loading invoice:', err);
      setError(err.response?.data?.detail || 'Preventivo non trovato');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAcceptAndRequestPaymentLink = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/checkout/emergency/${invoiceId}/request-payment-link`, {
        send_sms: true,
        send_email: true
      });
      
      toast.success('Link di pagamento inviato via SMS e Email!');
      setPaymentSent(true);
    } catch (err) {
      console.error('Error requesting payment link:', err);
      toast.error(err.response?.data?.detail || 'Errore invio link');
    } finally {
      setProcessing(false);
    }
  };
  
  const handlePayNow = async () => {
    setProcessing(true);
    try {
      // Create a Stripe checkout session
      const res = await axios.post(`${API}/checkout/emergency/${invoiceId}/create-payment`, {
        success_url: `${window.location.origin}/checkout/emergency/${invoiceId}/success`,
        cancel_url: window.location.href
      });
      
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else if (res.data.demo_mode) {
        // Demo mode - simulate payment
        toast.success('Pagamento demo completato!');
        setPaymentSent(true);
        
        // Notify backend
        await axios.post(`${API}/checkout/emergency/${invoiceId}/confirm-demo-payment`);
      }
    } catch (err) {
      console.error('Error creating payment:', err);
      toast.error(err.response?.data?.detail || 'Errore pagamento');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleStartVisitFree = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/checkout/emergency/${invoiceId}/confirm-free`);
      toast.success('Check-in completato! Accomodati in sala d\'attesa.');
      setPaymentSent(true);
    } catch (err) {
      console.error('Error confirming free visit:', err);
      toast.error(err.response?.data?.detail || 'Errore conferma');
    } finally {
      setProcessing(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#daeef9] via-[#f0f7fc] to-[#d4e8f5] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto" style={{color: '#2A9FCB'}} />
          <p className="mt-4 text-gray-600">Caricamento preventivo...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#daeef9] via-[#f0f7fc] to-[#d4e8f5] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
          <h1 className="text-xl font-bold text-gray-800 mb-2">Errore</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 rounded-xl text-white font-medium"
            style={{background: 'linear-gradient(135deg, #2A9FCB, #2A9FCB)'}}
          >
            Torna alla Home
          </button>
        </div>
      </div>
    );
  }
  
  if (paymentSent || invoice?.status === 'paid' || invoice?.status === 'accepted') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#daeef9] via-[#f0f7fc] to-[#d4e8f5] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
               style={{background: 'linear-gradient(135deg, #10B981, #059669)'}}>
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{color: '#2A9FCB'}}>
            {invoice?.status === 'paid' ? 'Pagamento Completato!' : 'Richiesta Confermata!'}
          </h1>
          <p className="text-gray-600 mb-6">
            {invoice?.status === 'paid' 
              ? 'Il tuo pagamento è stato ricevuto. Accomodati in sala d\'attesa, verrai chiamato appena possibile.'
              : invoice?.amount_to_pay === 0 
                ? 'La tua visita è coperta dall\'abbonamento. Accomodati in sala d\'attesa.'
                : 'Riceverai a breve un link di pagamento via SMS e Email.'}
          </p>
          
          <div className="bg-sky-50 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-center gap-2 text-sky-700">
              <Clock className="w-5 h-5" />
              <span className="font-medium">Sala d'Attesa</span>
            </div>
            <p className="text-sm text-sky-600 mt-2">
              Verrai chiamato dal medico appena sarà il tuo turno
            </p>
          </div>
          
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 rounded-xl border hover:bg-gray-50 transition-colors"
          >
            Chiudi
          </button>
        </div>
      </div>
    );
  }
  
  const amountToPay = invoice?.amount_to_pay || 0;
  const isCovered = amountToPay === 0;
  const hasSubscription = invoice?.subscription_discount > 0;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#daeef9] via-[#f0f7fc] to-[#d4e8f5] py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <img src="/logo.png" alt="Prime Vision" className="h-12 mx-auto mb-4" 
               onError={(e) => e.target.style.display = 'none'} />
          <h1 className="text-2xl font-bold" style={{color: '#2A9FCB', fontFamily: "'Lora', Georgia, serif"}}>
            Preventivo Visita Urgente
          </h1>
          <p className="text-gray-500 text-sm mt-1">Codice: {invoice?.invoice_code}</p>
        </div>
        
        {/* Patient Info */}
        {patient && (
          <div className="bg-white rounded-xl p-4 mb-4 shadow-sm">
            <p className="text-sm text-gray-500">Paziente</p>
            <p className="font-semibold text-gray-800">
              {patient.first_name} {patient.last_name}
            </p>
          </div>
        )}
        
        {/* Services List */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-4">
          <div className="p-4 border-b" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
            <h2 className="font-semibold flex items-center gap-2" style={{color: '#2A9FCB'}}>
              <Receipt className="w-5 h-5" />
              Prestazioni da Erogare
            </h2>
          </div>
          
          <div className="p-4 space-y-3">
            {invoice?.items?.map((item, idx) => (
              <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                <div>
                  <p className="font-medium text-gray-800">{item.service_name}</p>
                  {item.service_id === 'emergency-fee' && (
                    <p className="text-xs text-red-500">Supplemento per urgenza</p>
                  )}
                </div>
                <span className="font-semibold" style={{color: '#2A9FCB'}}>
                  {item.price?.toFixed(2)} €
                </span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Subscription Discount */}
        {hasSubscription && (
          <div className="bg-green-50 rounded-xl p-4 mb-4 border border-green-200">
            <div className="flex items-center gap-2 text-green-700 mb-2">
              <ShieldCheck className="w-5 h-5" />
              <span className="font-semibold">Abbonamento {invoice?.subscription_plan?.toUpperCase()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Sconto abbonamento:</span>
              <span className="font-bold text-green-600">-{invoice?.subscription_discount?.toFixed(2)} €</span>
            </div>
            {invoice?.covered_services?.length > 0 && (
              <p className="text-xs text-green-600 mt-2">
                Prestazioni coperte: {invoice.covered_services.join(', ')}
              </p>
            )}
          </div>
        )}
        
        {/* Total */}
        <div className="rounded-2xl p-6 mb-6"
             style={{background: 'linear-gradient(135deg, #2A9FCB, #2A9FCB)'}}>
          <div className="flex justify-between items-center text-white">
            <div>
              <p className="text-sm opacity-80">Totale da Pagare</p>
              <p className="text-3xl font-bold">{amountToPay.toFixed(2)} €</p>
            </div>
            {isCovered && (
              <div className="bg-white/20 rounded-full px-4 py-2">
                <span className="text-sm font-medium">Coperto</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="space-y-3">
          {isCovered ? (
            // Fully covered by subscription
            <button
              onClick={handleStartVisitFree}
              disabled={processing}
              className="w-full py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-50"
              style={{background: 'linear-gradient(135deg, #10B981, #059669)'}}
              data-testid="btn-start-visit-free"
            >
              {processing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Inizia la Visita - Accomodati in Sala d'Attesa
                </>
              )}
            </button>
          ) : (
            <>
              {/* Pay Now */}
              <button
                onClick={handlePayNow}
                disabled={processing}
                className="w-full py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-50"
                style={{background: 'linear-gradient(135deg, #2A9FCB, #2A9FCB)'}}
                data-testid="btn-pay-now"
              >
                {processing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    Paga Subito - {amountToPay.toFixed(2)} €
                  </>
                )}
              </button>
              
              {/* Request Payment Link */}
              <button
                onClick={handleAcceptAndRequestPaymentLink}
                disabled={processing}
                className="w-full py-4 rounded-xl font-semibold border-2 flex items-center justify-center gap-2 transition-all hover:bg-gray-50 disabled:opacity-50"
                style={{borderColor: '#2A9FCB', color: '#2A9FCB'}}
                data-testid="btn-request-link"
              >
                {processing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <div className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      <Phone className="w-4 h-4" />
                    </div>
                    Ricevi Link di Pagamento via SMS ed Email
                  </>
                )}
              </button>
            </>
          )}
        </div>
        
        {/* Info Box */}
        <div className="mt-6 p-4 rounded-xl bg-amber-50 border border-amber-200">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium mb-1">Informazioni</p>
              <p>
                {isCovered 
                  ? 'Le prestazioni sono coperte dal tuo abbonamento. Premi il pulsante per confermare e accomodati in sala d\'attesa.'
                  : 'Puoi pagare subito oppure richiedere il link di pagamento che riceverai via SMS e Email. Una volta completato il pagamento, verrai chiamato in visita.'}
              </p>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="text-center mt-8 text-xs text-gray-400">
          <p>Prime Vision - Centro di Oculistica Pediatrica</p>
          <p>Via Roma 123, Milano - Tel. 02 1234567</p>
        </div>
      </div>
    </div>
  );
};

export default EmergencyCheckout;
